import { useEffect, useMemo, useState } from 'react';
import PersonAvatar from '../../components/people/PersonAvatar';
import { Ban, CalendarDays, Check, ChevronLeft, Loader2, Search, Users } from 'lucide-react';
import {
  getMeetlyCredits,
  getMeetlyLimits,
  getMeetlyUsage,
  setMeetlyAccess,
  setMeetlyLimit,
  type MeetlyCredits,
  type MeetlyLimits,
  type MeetlyUsage,
} from '../../api/meetly';
import MeetlyCreditMeter from '../../components/MeetlyCreditMeter';

const number = new Intl.NumberFormat('en-IN');
const dateValue = (date: Date) => date.toISOString().slice(0, 10);
const daysAgo = (days: number) => dateValue(new Date(Date.now() - days * 86_400_000));

type LoadState = 'loading' | 'ready' | 'failed';

export default function MeetlyUsageSettings() {
  const [credits, setCredits] = useState<MeetlyCredits | null>(null);
  const [usage, setUsage] = useState<MeetlyUsage | null>(null);
  const [limits, setLimits] = useState<MeetlyLimits | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [accessSaving, setAccessSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = async (rangeStart = start, rangeEnd = end) => {
    setLoadState('loading');
    try {
      const [nextCredits, nextUsage, nextLimits] = await Promise.all([
        getMeetlyCredits(),
        getMeetlyUsage(rangeStart || undefined, rangeEnd || undefined),
        getMeetlyLimits(),
      ]);
      setCredits(nextCredits.data);
      setUsage(nextUsage.data);
      setLimits(nextLimits.data);
      setDrafts(Object.fromEntries(nextLimits.data.people.map(person => [person.user_id, String(person.monthly_credits)])));
      setLoadState('ready');
    } catch {
      setLoadState('failed');
    }
  };

  useEffect(() => { void load(); }, []); // Load authorization scope from the server, never from a client role list.

  const setPreset = (days: number | null) => {
    const nextStart = days === null ? '' : daysAgo(days - 1);
    const nextEnd = days === null ? '' : dateValue(new Date());
    setStart(nextStart);
    setEnd(nextEnd);
    void load(nextStart, nextEnd);
  };

  const people = useMemo(() => {
    if (!usage || !limits) return [];
    const names = new Map(usage.people.map(person => [person.user_id, person.name]));
    return limits.people.map(limit => ({ ...limit, name: names.get(limit.user_id) || limit.user_id }));
  }, [usage, limits]);
  const filteredPeople = people.filter(person => person.name.toLowerCase().includes(search.trim().toLowerCase()));
  // Teamlio's company-wide ceiling binds an admin too — set_meetly_limit refuses
  // anything above it — so the input must not invite a number the API will send
  // straight back. A manager stays capped by their own allowance as well.
  const workspaceCap = limits?.self.workspace?.limit ?? null;
  const branchMaximum = limits?.scope === 'branch' ? 20_000 : (limits?.self.limit || 0);
  const maximum = workspaceCap == null ? branchMaximum : Math.min(branchMaximum, workspaceCap);
  const maxDayTokens = Math.max(1, ...(usage?.by_day.map(day => day.total_tokens) || [0]));
  const enabledCount = people.filter(person => person.meetly_enabled).length;

  const toggleAccess = async (userId: string, name: string, enabled: boolean) => {
    setSaveError(null);
    setAccessSaving(userId);
    try {
      await setMeetlyAccess(userId, enabled);
      await load();
    } catch {
      setSaveError(`Could not ${enabled ? 'enable' : 'turn off'} Meetly for ${name}. Check your connection and try again.`);
    } finally {
      setAccessSaving(null);
    }
  };

  const saveLimit = async (userId: string) => {
    const value = Number(drafts[userId]);
    if (!Number.isInteger(value) || value < 0 || value > maximum) {
      setSaveError(`Enter a whole number between 0 and ${number.format(maximum)}.`);
      return;
    }
    setSaveError(null);
    setSaving(userId);
    try {
      await setMeetlyLimit(userId, value);
      await load();
    } catch {
      setSaveError('Could not save this limit. Check your connection and try again.');
    } finally {
      setSaving(null);
    }
  };

  if (loadState === 'loading') {
    return <main className="gt-shell min-h-screen p-5 md:p-7"><section className="gt-card mx-auto flex min-h-[260px] max-w-3xl items-center justify-center gap-3 text-sm text-[var(--gt-text-secondary)]"><Loader2 className="animate-spin text-[var(--gt-primary)]" size={18} /> Loading Meetly usage…</section></main>;
  }
  if (loadState === 'failed' || !usage || !limits) {
    return <main className="gt-shell min-h-screen p-5 md:p-7"><section role="alert" className="gt-card mx-auto max-w-3xl p-7 text-center"><h1 className="gt-display text-2xl text-[var(--gt-text-primary)]">Usage unavailable</h1><p className="mt-2 text-sm text-[var(--gt-text-secondary)]">Meetly could not load your usage and limits.</p><button type="button" onClick={() => void load()} className="mt-5 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white">Try again</button></section></main>;
  }

  return (
    <main className="gt-shell min-h-screen">
      <div className="mx-auto w-full max-w-[1280px] p-4 md:p-7">
        <header className="flex flex-col gap-4 border-b border-[var(--gt-divider)] pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <button type="button" onClick={() => window.history.back()} className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--gt-text-secondary)] hover:text-[var(--gt-primary)]"><ChevronLeft size={16} /> Meeting workspace</button>
            <p className="gt-eyebrow mt-4">Meetly / Credits & usage</p>
            <h1 className="gt-display mt-2 text-3xl text-[var(--gt-text-primary)]">Your meeting allowance</h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-[var(--gt-text-secondary)]">One completed meeting uses one monthly credit. Token totals show the transcription and summary work behind it.</p>
          </div>
          {credits && <div className="min-w-[320px]"><MeetlyCreditMeter credits={credits} /></div>}
        </header>

        <section className="mt-7" aria-labelledby="usage-range-heading">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="usage-range-heading" className="mr-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--gt-text-tertiary)]">Token usage</h2>
            {[[1, 'Today'], [7, '7 days'], [30, '30 days'], [90, '90 days']].map(([days, label]) => <button key={String(days)} type="button" onClick={() => setPreset(Number(days))} className="rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-secondary)] hover:bg-[var(--gt-surface-1)]">{label}</button>)}
            <button type="button" onClick={() => setPreset(null)} className="rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-secondary)] hover:bg-[var(--gt-surface-1)]">All time</button>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold text-[var(--gt-text-secondary)]">From<input aria-label="Usage start date" type="date" value={start} onChange={event => setStart(event.target.value)} className="mt-1 block rounded-xl border border-[var(--gt-border-strong)] bg-white px-3 py-2 text-sm text-[var(--gt-text-primary)]" /></label>
            <label className="text-xs font-semibold text-[var(--gt-text-secondary)]">To<input aria-label="Usage end date" type="date" value={end} onChange={event => setEnd(event.target.value)} className="mt-1 block rounded-xl border border-[var(--gt-border-strong)] bg-white px-3 py-2 text-sm text-[var(--gt-text-primary)]" /></label>
            <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-full bg-[var(--gt-primary)] px-4 py-2 text-sm font-bold text-white"><CalendarDays size={15} /> Apply range</button>
          </div>
        </section>

        <section className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-label="Usage totals">
          {[
            ['Total tokens', usage.totals.total_tokens], ['Meetings', usage.totals.messages], ['AI calls', usage.totals.calls], ['Failed', usage.totals.failed], ['Prompt / reply', `${number.format(usage.totals.prompt_tokens)} / ${number.format(usage.totals.completion_tokens)}`],
          ].map(([label, value]) => <article key={String(label)} className="rounded-2xl border border-[var(--gt-border)] bg-white p-4"><p className="text-xs font-bold uppercase tracking-[0.1em] text-[var(--gt-text-tertiary)]">{label}</p><p className="mt-2 text-xl font-bold tabular-nums text-[var(--gt-primary)]">{typeof value === 'number' ? number.format(value) : value}</p></article>)}
        </section>

        <section className="gt-card mt-7 p-5" aria-labelledby="daily-usage-heading">
          <h2 id="daily-usage-heading" className="text-sm font-bold text-[var(--gt-text-primary)]">Tokens per day</h2>
          <div className="mt-5 flex h-36 items-stretch gap-1.5 border-b border-[var(--gt-divider)]" aria-label="Daily token usage chart">
            {usage.by_day.length ? usage.by_day.map(day => <div key={day.date} title={`${day.date}: ${number.format(day.total_tokens)} tokens`} className="group flex h-full min-w-4 flex-1 flex-col justify-end"><span className="rounded-t-md bg-[var(--gt-primary)] transition-opacity group-hover:opacity-75" style={{ height: `${Math.max(4, (day.total_tokens / maxDayTokens) * 100)}%` }} /><span className="sr-only">{day.date}: {number.format(day.total_tokens)} tokens</span></div>) : <p className="pb-4 text-sm text-[var(--gt-text-tertiary)]">No token usage in this period.</p>}
          </div>
        </section>

        <section className="gt-card mt-7 overflow-hidden" aria-labelledby="people-heading">
          <div className="flex flex-col gap-3 border-b border-[var(--gt-divider)] p-5 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="gt-eyebrow">{limits.scope === 'branch' ? 'Branch-wide' : limits.scope === 'team' ? 'Direct reports' : 'Your usage'}</p><h2 id="people-heading" className="mt-1 text-lg font-bold text-[var(--gt-text-primary)]">People & limits</h2>{limits.scope !== 'self' && <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">{enabledCount} of {people.length} {people.length === 1 ? 'person has' : 'people have'} Meetly access. Turn it on for anyone here, then give them monthly meetings.</p>}{limits.self.workspace && <p className="mt-1 text-sm font-semibold text-[var(--gt-text-secondary)]">Workspace allowance: {limits.self.workspace.used + limits.self.workspace.reserved} of {limits.self.workspace.limit} meetings used this month. Allocate within it &mdash; only Teamlio can raise it.</p>}</div>
            {limits.scope !== 'self' && <label className="flex items-center gap-2 rounded-xl border border-[var(--gt-border)] bg-[var(--gt-surface-1)] px-3 py-2"><Search size={15} className="text-[var(--gt-text-tertiary)]" /><span className="sr-only">Search people</span><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search people" aria-label="Search people" className="w-40 bg-transparent text-sm outline-none" /></label>}
          </div>
          {limits.scope === 'self' ? <div className="p-5 text-sm text-[var(--gt-text-secondary)]">Your administrator manages this allowance. Your meter above shows the current balance and reset date.</div> : <div className="divide-y divide-[var(--gt-divider)]">
            {filteredPeople.map(person => {
              const busy = accessSaving === person.user_id;
              return (
                <div key={person.user_id} className="flex flex-col gap-4 p-5 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <PersonAvatar userId={person.user_id} name={person.name} size={28} />
                      <p className="font-bold text-[var(--gt-text-primary)]">{person.name}</p>
                      {person.meetly_enabled
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800"><Check size={12} /> Meetly on</span>
                        : <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gt-surface-1)] px-2.5 py-1 text-xs font-bold text-[var(--gt-text-tertiary)]"><Ban size={12} /> No Meetly access</span>}
                    </div>
                    <p className="mt-1 text-sm text-[var(--gt-text-secondary)]">{number.format(usage.people.find(item => item.user_id === person.user_id)?.total_tokens || 0)} tokens in selected period</p>
                  </div>
                  <div className="flex flex-wrap items-end gap-4">
                    {person.can_manage_access && (
                      <div className="flex w-24 flex-col gap-1">
                        <span className="text-xs font-semibold text-[var(--gt-text-secondary)]">Meetly access</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={person.meetly_enabled}
                          aria-label={`Meetly access for ${person.name}`}
                          disabled={busy}
                          onClick={() => void toggleAccess(person.user_id, person.name, !person.meetly_enabled)}
                          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${person.meetly_enabled ? 'bg-[var(--gt-primary)]' : 'bg-[var(--gt-border-strong)]'}`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${person.meetly_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          {busy && <Loader2 size={12} className="absolute -right-5 animate-spin text-[var(--gt-text-tertiary)]" />}
                        </button>
                      </div>
                    )}
                    {person.can_edit
                      ? <div className="flex items-end gap-2">
                          <label className="text-xs font-semibold text-[var(--gt-text-secondary)]">Monthly meetings<input aria-label={`Monthly meetings for ${person.name}`} type="number" min={0} max={maximum} value={drafts[person.user_id] ?? ''} onChange={event => setDrafts(current => ({ ...current, [person.user_id]: event.target.value }))} className="mt-1 block w-28 rounded-xl border border-[var(--gt-border-strong)] bg-white px-3 py-2 text-sm text-[var(--gt-text-primary)]" /></label>
                          <button type="button" onClick={() => void saveLimit(person.user_id)} disabled={saving === person.user_id} className="inline-flex min-h-10 items-center gap-1 rounded-full bg-[var(--gt-primary)] px-4 text-sm font-bold text-white disabled:opacity-60"><span className="sr-only">Save limit for {person.name}</span>{saving === person.user_id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} <span aria-hidden="true">Save limit</span></button>
                        </div>
                      : <p className="inline-flex items-center gap-2 rounded-full bg-[var(--gt-surface-1)] px-3 py-2 text-sm font-semibold text-[var(--gt-text-secondary)]"><Users size={15} /> {person.locked_by_admin ? 'Managed by Admin' : 'View only'}</p>}
                  </div>
                </div>
              );
            })}
            {!filteredPeople.length && <p className="p-5 text-sm text-[var(--gt-text-tertiary)]">No people match this search.</p>}
          </div>}
          {saveError && <p role="alert" className="border-t border-red-100 bg-red-50 px-5 py-3 text-sm text-red-800">{saveError}</p>}
        </section>
      </div>
    </main>
  );
}
